import Card from "./Card";
import { cn } from "../../lib/cn";

/**
 * Phase 2 primitive — `<KpiCard>` is the label + big number + optional
 * trend/subtitle tile used across Dashboard, Financial, My Tasks, and
 * Resources. Replaces `.kpi` + `.kpi-lbl` + `.kpi-val` + `.kpi-trend` +
 * `.kpi-sub` from common-ui.css and the `mt-kpi-*` family from My Tasks.
 *
 * Props:
 *   label     — uppercase caption (`.kpi-lbl` equivalent)
 *   value     — big mono number (`.kpi-val`)
 *   trend     — optional { direction: 'up'|'down'|'flat', text }
 *   sub       — optional secondary text below the value
 *   tone      — colours the value text: default|success|warn|crit|info
 *   onClick   — makes the card interactive (hover-lift + top accent bar)
 */
const VALUE_TONE = {
  default: "text-rc-text-primary",
  info:    "text-rc-accent-strong",
  success: "text-[#10c981]",
  warn:    "text-[#f59e0b]",
  crit:    "text-rc-red",
};

const TREND_TONE = {
  up:   "text-[#10c981]",
  down: "text-rc-red",
  flat: "text-rc-text-dim",
};

const TREND_ARROW = { up: "▲", down: "▼", flat: "·" };

export default function KpiCard({ label, value, trend, sub, tone = "default", onClick, className }) {
  const variant = onClick ? "interactive" : "kpi";
  return (
    <Card variant={variant} onClick={onClick} className={className}>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.1em] text-rc-text-secondary">
        {label}
      </div>
      <div className={cn(
        "font-mono font-extrabold text-[26px] leading-none tracking-[-0.02em]",
        VALUE_TONE[tone] || VALUE_TONE.default,
      )}>
        {value}
      </div>
      {trend && (
        <div className={cn(
          "mt-2 flex items-center gap-1 text-[13px] font-semibold",
          TREND_TONE[trend.direction] || TREND_TONE.flat,
        )}>
          <span>{TREND_ARROW[trend.direction] || TREND_ARROW.flat}</span>
          <span>{trend.text}</span>
        </div>
      )}
      {sub && <div className="mt-1.5 text-xs text-rc-text-dim">{sub}</div>}
    </Card>
  );
}
